import { type IntlShape, defineMessages } from 'react-intl';

const MINUTE = 60000;
const HOUR = 3600000;
const DAY = 86400000;
const WEEK = 604800000;
const MONTH = 2592000000;
const YEAR = 31536000000;

export const relativeTimeMessages = defineMessages({
	justNow: {
		id: 'macro-utils.relative-time-just-now',
		defaultMessage: 'just now',
		description: 'Relative time label shown when the activity happened less than a minute ago.',
	},
	minutesAgo: {
		id: 'macro-utils.relative-time-minutes-ago',
		defaultMessage:
			'{minuteCount, plural, one {{minuteCount} minute ago} other {{minuteCount} minutes ago}}',
		description: 'Relative time label for minutes ago. {minuteCount} is the number of minutes.',
	},
	hoursAgo: {
		id: 'macro-utils.relative-time-hours-ago',
		defaultMessage: '{hourCount, plural, one {{hourCount} hour ago} other {{hourCount} hours ago}}',
		description: 'Relative time label for hours ago. {hourCount} is the number of hours.',
	},
	daysAgo: {
		id: 'macro-utils.relative-time-days-ago',
		defaultMessage: '{dayCount, plural, one {{dayCount} day ago} other {{dayCount} days ago}}',
		description: 'Relative time label for days ago. {dayCount} is the number of days.',
	},
	weeksAgo: {
		id: 'macro-utils.relative-time-weeks-ago',
		defaultMessage: '{weekCount, plural, one {{weekCount} week ago} other {{weekCount} weeks ago}}',
		description: 'Relative time label for weeks ago. {weekCount} is the number of weeks.',
	},
	monthsAgo: {
		id: 'macro-utils.relative-time-months-ago',
		defaultMessage:
			'{monthCount, plural, one {{monthCount} month ago} other {{monthCount} months ago}}',
		description: 'Relative time label for months ago. {monthCount} is the number of months.',
	},
	yearsAgo: {
		id: 'macro-utils.relative-time-years-ago',
		defaultMessage: '{yearCount, plural, one {{yearCount} year ago} other {{yearCount} years ago}}',
		description: 'Relative time label for years ago. {yearCount} is the number of years.',
	},
});

export const getRelativeTime = (timestamp: number, intl: IntlShape): string => {
	const now = Date.now();
	const diff = now - timestamp;

	if (diff < MINUTE) {
		return intl.formatMessage(relativeTimeMessages.justNow);
	} else if (diff < HOUR) {
		return intl.formatMessage(relativeTimeMessages.minutesAgo, {
			minuteCount: Math.floor(diff / MINUTE),
		});
	} else if (diff < DAY) {
		return intl.formatMessage(relativeTimeMessages.hoursAgo, {
			hourCount: Math.floor(diff / HOUR),
		});
	} else if (diff < WEEK) {
		return intl.formatMessage(relativeTimeMessages.daysAgo, {
			dayCount: Math.floor(diff / DAY),
		});
	} else if (diff < MONTH) {
		return intl.formatMessage(relativeTimeMessages.weeksAgo, {
			weekCount: Math.floor(diff / WEEK),
		});
	} else if (diff < YEAR) {
		return intl.formatMessage(relativeTimeMessages.monthsAgo, {
			monthCount: Math.floor(diff / MONTH),
		});
	} else {
		return intl.formatMessage(relativeTimeMessages.yearsAgo, {
			yearCount: Math.floor(diff / YEAR),
		});
	}
};
